/**
 * A short Messenger-style pop for an arriving reply, synthesised with the Web
 * Audio API rather than shipping an audio file -- there is nothing to bundle,
 * license, or fail to load onto someone else's site.
 *
 * One shared context reused across calls: creating a new `AudioContext` per
 * play leaks a handle and, on some browsers, needs its own unlock gesture
 * every time.
 */
let ctx: AudioContext | null = null;

function ensureContext(): AudioContext {
  ctx ??= new AudioContext();
  return ctx;
}

/**
 * Unlock audio on the visitor's very first interaction with the page.
 *
 * A context created with no prior user gesture starts `suspended`, and a
 * suspended context's oscillators run start()/stop() without error but
 * produce no sound at all -- there is nothing to catch, so the chime that
 * `playNotifySound` schedules for a reply arriving minutes later plays into
 * silence. Priming here, from a real click/keydown/touch rather than a
 * server-pushed event, is what actually satisfies the browser's autoplay
 * policy in time for that later, unattended call to work.
 */
export function primeNotifySound(): void {
  try {
    void ensureContext().resume();
  } catch {
    // No Web Audio in this browser -- playNotifySound's own try/catch covers
    // the rest.
  }
}

export function playNotifySound(): void {
  try {
    const audio = ensureContext();
    // Covers the case where no prior interaction ever primed the context
    // (e.g. the widget mounted and a reply arrived before the visitor
    // touched the page at all) -- harmless to call when already running.
    if (audio.state === 'suspended') void audio.resume();
    const now = audio.currentTime;
    // A Messenger-style "pop": two short bubbles, each a quick upward pitch
    // glide with a fast decay, the second a fifth higher. The glide is what
    // makes it read as a bubble rather than a flat beep.
    const pops: [number, number, number][] = [
      // [delay, start Hz, end Hz]
      [0, 520, 880],
      [0.085, 780, 1320],
    ];
    for (const [delay, from, to] of pops) {
      const t = now + delay;
      const osc = audio.createOscillator();
      const gain = audio.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(from, t);
      osc.frequency.exponentialRampToValueAtTime(to, t + 0.045);
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(0.25, t + 0.008);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.12);
      osc.connect(gain).connect(audio.destination);
      osc.start(t);
      osc.stop(t + 0.13);
    }
  } catch {
    // No Web Audio, or the visitor's browser hasn't granted audio yet -- a
    // missed chime isn't worth an error.
  }
}
